import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {FlowState} from '@gongxigames/framework/slot/modules/flow/states/FlowState';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {FlowModule} from '@gongxigames/framework/slot/modules/flow/FlowModule';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';
import {WinMeterEvents} from '@gongxigames/framework/slot/modules/winmeter';

export class FlowRestoreState extends FlowState {
    private awaitedEventBeforeRestore?: string | symbol;
    protected setupEvents(): void {
        super.setupEvents();

        this.setEventHandler('General.Game.STARTED', this.onGameStarted.bind(this));
        this.setEventHandler('General.Flow.WAIT_EVENT_BEFORE_RESTORE_GAME', this.onWaitEventBeforeRestoreGame.bind(this));
    }

    private onWaitEventBeforeRestoreGame(event: string | symbol): void {
        this.awaitedEventBeforeRestore = event;
        this.addCallbackToWaitingLIst(event, this.onEmittedWaitingEventBeforeRestoreGame.bind(this));
    }

    private onEmittedWaitingEventBeforeRestoreGame(): void {
        this.awaitedEventBeforeRestore = undefined;
    }

    public onEnterState(): void {
    }

    public onLeaveState(): void {
    }

    protected onGameStarted(): void {
        this.showRestorePopup();
    }

    protected showRestorePopup(): void {
        const popup = TemplateUtils.createPopup('restore-popup', this.module.languageService.get('gameRestored'));
        const button = document.createElement('button');

        const systemPopup = {
            id: 'restore-popup',
            priority: popup.priority,
            view: popup,
            type: 'HTML'
        };

        button.textContent = this.module.languageService.get('clickToContinue');
        button.onclick = () => {
            this.emit('General.Popups.Remove', systemPopup);
            this.onRestorePopupClosed();
        };

        popup.content.insertAdjacentElement('beforeend', button);
        popup.content.classList.add('long-buttons');
        popup.closeButton.hidden = true;

        this.emit('General.Popups.Add', systemPopup);
    }

    protected onRestorePopupClosed(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            this.startRestoreGame();
        } else {
            this.addToWaitingForResources(this.startRestoreGame.bind(this));
        }
    }

    private startRestoreGame(): void {
        if (this.awaitedEventBeforeRestore) {
            this.addCallbackToWaitingLIst(this.awaitedEventBeforeRestore, this.restoreGame.bind(this));
            this.emit('General.Flow.WAITING_OF_RESTORE_GAME');

            return;
        }

        this.restoreGame();
        this.emit('General.Game.RESTORED');
    }

    protected restoreGame(): void {
        this.restoreWinMeterValue();
        this.emit(FlowModuleEvents.BET_RESTORE, this.module.logic.getSessionBet());

        switch (this.module.logic.nextAction) {
            case Action.FREESPIN: {
                this.restoreFreespins();
                break;
            }

            case Action.RESPIN: {
                this.restoreRespin();
                break;
            }

            case Action.SELECT: {
                this.restoreSelectBonus();
                break;
            }

            case Action.PICK: {
                this.restorePickBonus();
                break;
            }

            default:
                this.module.gotoState(FlowModule.BASIC_STATE);
                break;
        }
    }

    protected restoreFreespins(): void {
        const prevSession = this.module.logic.getPreviousRoundSession();
        const fsWon: number = this.module.logic.getFreeGamesCount();
        const isFsStart = prevSession && this.module.logic.getFreeGamesIndex() === 0;

        if (isFsStart) {
            this.emit(FlowModuleEvents.FREESPINS_WIN, fsWon);
        } else {
            this.emit('General.UI.DISABLE', UIDisableReason.TRIGGERING_FEATURE);
            this.emit(FlowModuleEvents.FREESPINS_RESTORED);
            this.emit(FlowModuleEvents.CAN_SPIN);
        }

        this.module.gotoState(FlowModule.FREE_SPINS_STATE);
    }

    protected restoreRespin(): void {
        const prevSession = this.module.logic.getPreviousRoundSession();
        const isOnFreeSpins = this.module.logic.isBonusOnFreeGames(Bonus.RESPIN_BONUS);
        const isRespinStart = prevSession && prevSession.action !== Action.RESPIN;

        if (isOnFreeSpins) {
            this.emit(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED);
            this.module.gotoState(FlowModule.FREESPINS_RESPIN_STATE);
        } else {
            this.module.gotoState(FlowModule.RESPIN_BASIC_STATE);
        }

        this.emit(FlowModuleEvents.RESTORE_RESPIN_WINNING_REELSET);
        this.emit(FlowModuleEvents.HAS_RESPIN_ON_NEXT_SPIN);

        this.emit(isRespinStart ? FlowModuleEvents.RESPIN_WIN : FlowModuleEvents.RESPIN_RESTORED);
    }

    protected restoreSelectBonus(): void {
        this.emit(FlowModuleEvents.SELECT_BONUS_START);
        this.emit('General.UI.DISABLE', UIDisableReason.TRIGGERING_FEATURE);

        this.module.gotoState(FlowModule.BASIC_STATE);
    }

    protected restorePickBonus(): void {
        const isBonusStart = this.module.logic.getPickedValues()!.length === 0;
        const isOnFreeSpins = this.module.logic.isBonusOnFreeGames();
        const currentState = isOnFreeSpins ? FlowModule.FREE_GAMES_PICK_STATE : FlowModule.PICK_STATE;

        if (isOnFreeSpins) {
            this.emit('General.UI.DISABLE', UIDisableReason.TRIGGERING_FEATURE);
            this.emit(FlowModuleEvents.FREESPINS_RESTORED);
        }

        if (isBonusStart) {
            this.emit(FlowModuleEvents.START_PICK_BONUS);
        } else {
            this.emit(FlowModuleEvents.RESTORE_PICK_BONUS);
        }

        this.module.gotoState(currentState);
    }

    protected restoreWinMeterValue(): void {
        if (this.module.logic.getTotalWin()) {
            this.emit(WinMeterEvents.RESTORE_VALUE, this.module.logic.getTotalWin());
        }
    }
}
